import { type DebouncedFunc } from 'lodash';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useRef } from 'react';

export function useDebounceCallback<T extends (...args: Parameters<T>) => void>(
  fn: T,
  delayMs: number,
): DebouncedFunc<T> {
  const fnRef = useRef(fn);
  const debouncedRef = useRef<DebouncedFunc<T> | null>(null);

  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  useEffect(() => {
    const debounced = debounce((...args: Parameters<T>) => {
      fnRef.current(...args);
    }, delayMs);
    debouncedRef.current = debounced;
    return () => debounced.cancel();
  }, [delayMs]);

  // Stable identity with working cancel/flush that delegate to the live
  // debounced instance (the previous cast left cancel/flush undefined).
  return useMemo(() => {
    const caller = (...args: Parameters<T>) => {
      debouncedRef.current?.(...args);
    };
    caller.cancel = () => debouncedRef.current?.cancel();
    caller.flush = () => debouncedRef.current?.flush();
    return caller as unknown as DebouncedFunc<T>;
  }, []);
}
